/**
 * Kleine hulpen voor de klantkaart v10. Alleen opmaak: alle cijfers komen
 * uit de backend (`app/services/klantkaart_cijfers.py`).
 */
import type { KlantReview } from "@/types/api";

import type { KaartVolgende } from "../../Klanten/types";
import { GESPREKKEN_LIMIET, type KaartGesprek, type KaartOrderTab, type KaartRetourTab } from "./api";

const TZ = "Europe/Amsterdam";

function alsDatum(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  // Een kale datum ("2026-10-14") is een dag, geen moment: middag houdt
  // hem in elke tijdzone op dezelfde dag.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "do 8 okt", met het jaar erbij als het een ander jaar is. */
export function dagDatum(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d) return "";
  const jaar = (x: Date) => x.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  return d
    .toLocaleDateString("nl-NL", {
      timeZone: TZ,
      weekday: "short",
      day: "numeric",
      month: "short",
      ...(jaar(d) === jaar(nu) ? {} : { year: "numeric" }),
    })
    .replace(/\./g, "");
}

function heeftTijd(iso: string): boolean {
  return !/^\d{4}-\d{2}-\d{2}$/.test(iso);
}

function uurMinuut(d: Date): string {
  return d.toLocaleTimeString("nl-NL", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function zelfdeDag(a: Date, b: Date): boolean {
  const dag = (x: Date) => x.toLocaleDateString("nl-NL", { timeZone: TZ });
  return dag(a) === dag(b);
}

/** "4 okt", met het jaar erbij als het een ander jaar is (sessie 177). */
export function dagKort(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d) return "";
  const jaar = (x: Date) => x.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  return d
    .toLocaleDateString("nl-NL", {
      timeZone: TZ,
      day: "numeric",
      month: "short",
      ...(jaar(d) === jaar(nu) ? {} : { year: "numeric" }),
    })
    .replace(/\./g, "");
}

/** "do 5 okt 11:52"; een kale datum krijgt geen tijd (sessie 177). */
export function dagDatumTijd(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d || !iso) return "";
  const dag = dagDatum(iso, nu);
  return heeftTijd(iso) ? `${dag} ${uurMinuut(d)}` : dag;
}

/** "5 okt 11:52"; een kale datum krijgt geen tijd (sessie 177). */
export function dagTijdKort(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d || !iso) return "";
  const dag = dagKort(iso, nu);
  return heeftTijd(iso) ? `${dag} ${uurMinuut(d)}` : dag;
}

/** Vandaag alleen "11:52", anders "5 okt 11:52" (sessie 177, bron van een stand). */
export function tijdOfDag(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d || !iso) return "";
  if (heeftTijd(iso) && zelfdeDag(d, nu)) return uurMinuut(d);
  return dagTijdKort(iso, nu);
}

/** De kalenderdag in Amsterdam als "2026-10-06", om dagen te vergelijken. */
function dagSleutel(d: Date): string {
  return d.toLocaleDateString("sv-SE", { timeZone: TZ });
}

export interface VolgendeTegel {
  label: string;
  waarde: string;
  /** Tweede regel in woorden, bijvoorbeeld "laatste bestelling geannuleerd". */
  extra: string | null;
  /** "verleden" staat rustig (geen oranje), "toekomst" is een te doen datum. */
  stand: "toekomst" | "verleden" | "onbekend";
}

/**
 * Tegel "Volgende bestelling" (sessie 186). Een verwachte datum in het
 * verleden is geen oranje datum maar een zin: "verwacht rond 6 okt, niet
 * besteld", met "laatste bestelling geannuleerd" als dat zo is. Nooit alleen
 * kleur: de stand staat altijd in woorden. Een datum in de toekomst blijft
 * zoals hij was.
 */
export function volgendeTegel(
  v: KaartVolgende | null | undefined,
  aantalBestellingen: number,
  nu: Date = new Date(),
): VolgendeTegel {
  const d = alsDatum(v?.datum);
  if (!v || v.soort === "onbekend" || !d) {
    // Besluit 75 (keuze 12): bij een enkele bestelling is er nog geen ritme.
    return {
      label: "Volgende bestelling",
      waarde: aantalBestellingen === 1 ? "nog onbekend" : "onbekend",
      extra: null,
      stand: "onbekend",
    };
  }
  // Sessie 186 herstel: de backend zegt zelf of de datum voorbij is (dag in
  // Amsterdam) en of er daarna besteld is, voor elke soort. Alleen een oud
  // antwoord zonder die velden valt terug op de browserklok en over_tijd.
  const verleden =
    typeof v.verleden === "boolean" ? v.verleden : dagSleutel(d) < dagSleutel(nu);
  if (verleden) {
    const geannuleerd = v.laatste_geannuleerd === true;
    const besteld =
      typeof v.besteld_na === "boolean" ? v.besteld_na : !v.over_tijd && !geannuleerd;
    return {
      label: "Volgende bestelling",
      waarde: `verwacht rond ${dagKort(v.datum, nu)}, ${besteld ? "besteld" : "niet besteld"}`,
      extra: geannuleerd ? "laatste bestelling geannuleerd" : null,
      stand: "verleden",
    };
  }
  return {
    label: `Volgende bestelling, ${v.soort === "berekend" ? "berekend" : "uit interval"}`,
    waarde: dagDatum(v.datum, nu),
    extra: null,
    stand: "toekomst",
  };
}

/** "mrt 2023" */
export function maandJaarKort(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  return d
    .toLocaleDateString("nl-NL", { timeZone: TZ, month: "short", year: "numeric" })
    .replace(/\./g, "");
}

/** Duur als klant: "nieuw", "7 maanden", "1 jaar", "2,5 jaar". */
export function duurWoord(maanden: number | null | undefined): string | null {
  if (maanden === null || maanden === undefined) return null;
  if (maanden < 1) return "nieuw";
  if (maanden < 12) return maanden === 1 ? "1 maand" : `${maanden} maanden`;
  const halve = Math.floor(maanden / 6) / 2;
  return `${String(halve).replace(".", ",")} jaar`;
}

export type Tab = "dier" | "bestelling" | "retour" | "contact" | "reviews";

// Werkplek 28-10 (JP): de meeste vragen gaan over bestellingen, dus
// Bestelling staat vooraan en is de standaardtab van elke zaak.
export const TABS: { id: Tab; label: string }[] = [
  { id: "bestelling", label: "Bestelling" },
  { id: "dier", label: "Dier" },
  { id: "retour", label: "Retour" },
  { id: "contact", label: "Contact" },
  { id: "reviews", label: "Reviews" },
];

const TAB_SLEUTEL = "ks-klantkaart10-tab";

/** Laatst gekozen tab, per sessie (sessionStorage; mag ontbreken). */
export function bewaardeTab(): Tab {
  try {
    const t = window.sessionStorage.getItem(TAB_SLEUTEL);
    if (TABS.some((x) => x.id === t)) return t as Tab;
  } catch {
    // privévenster of geblokkeerde opslag: gewoon de eerste tab
  }
  return TABS[0].id;
}

export function bewaarTab(tab: Tab): void {
  try {
    window.sessionStorage.setItem(TAB_SLEUTEL, tab);
  } catch {
    // niets: onthouden is gemak, geen voorwaarde
  }
}

// ---------------------------------------------------------------------------
// Sessie 141-E: tabtellers, eerdere contacten, stand per bestelling, reviews
// ---------------------------------------------------------------------------

/** De kanalen die als contact tellen; herbestel-gebeurtenissen niet. */
export const CONTACT_KANALEN = ["mail", "chat", "whatsapp", "telefoon"] as const;

export const KANAAL_LABEL: Record<(typeof CONTACT_KANALEN)[number], string> = {
  mail: "Mail",
  chat: "Chat",
  whatsapp: "WhatsApp",
  telefoon: "Telefoon",
};

/** De contacten uit de route, nieuwste eerst; zonder herbestel-regels. */
export function contacten(lijst: KaartGesprek[]): KaartGesprek[] {
  return lijst
    .filter((g) => (CONTACT_KANALEN as readonly string[]).includes(g.kanaal))
    .sort((a, b) => (a.moment < b.moment ? 1 : a.moment > b.moment ? -1 : 0));
}

/**
 * Het aantal contacten als tekst, met "+" als de route op zijn limiet zat
 * (dan zijn er misschien meer). Nul: `null`, dan geen teller.
 */
export function contactAantal(lijst: KaartGesprek[] | undefined): string | null {
  if (!lijst) return null;
  const aantal = contacten(lijst).length;
  if (aantal === 0) return null;
  return `${aantal}${lijst.length >= GESPREKKEN_LIMIET ? "+" : ""}`;
}

/** "Mail: vraag over bezorgdag, door Sanne"; zonder bekende naam geen "door". */
export function contactRegel(g: KaartGesprek): string {
  const kanaal = KANAAL_LABEL[g.kanaal as keyof typeof KANAAL_LABEL] ?? g.kanaal;
  const onderwerp = g.titel?.trim() || "zonder onderwerp";
  return `${kanaal}: ${onderwerp}${g.door ? `, door ${g.door}` : ""}`;
}

/**
 * De stand van een bestelling in de compacte regel (mock: "bezorgd",
 * "retour, smaakwissel"). Geannuleerd blijft geannuleerd; een retour op
 * deze order (niet geannuleerd) geeft "retour" met de reden; anders de
 * laatste gezette stap van de tijdlijn ("bezorgd", "verzonden"). Geen stap
 * gezet: het statuslabel van de order, of "stand onbekend".
 */
export function bestelStand(order: KaartOrderTab, retouren: KaartRetourTab[] = []): string {
  if (order.geannuleerd) return "geannuleerd";
  const retour = retouren.find(
    (r) => r.ordernummer === order.ordernummer && r.status !== "geannuleerd",
  );
  if (retour) return retour.reden ? `retour, ${retour.reden}` : "retour";
  const laatste = [...order.stappen].reverse().find((s) => s.gedaan);
  if (laatste) return laatste.label.toLowerCase();
  return order.status_label ? order.status_label.toLowerCase() : "stand onbekend";
}

/**
 * Het gemiddelde op vijf over de reviews met een score: "4,8", "5" (zonder
 * ",0"). Zelfde schaal als elke regel ("5 van 5", `scoreWoord`), zodat er
 * geen twee schalen onder elkaar staan. Geen enkele score: `null`.
 */
export function gemiddeldOpVijf(reviews: KlantReview[]): string | null {
  const scores = reviews
    .filter((r) => r.review_score != null && r.score_max > 0)
    .map((r) => ((r.review_score as number) / r.score_max) * 5);
  if (scores.length === 0) return null;
  const gem = Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
  return String(gem).replace(".", ",");
}

/**
 * TAP-tickets 28-10: de stand van een ticket bij Cenfil als woord (nooit
 * alleen kleur). Een waarde die we niet kennen heet "Onbekend".
 */
export function ticketStand(status: string | null | undefined): string {
  switch ((status ?? "").trim().toLowerCase()) {
    case "open":
      return "Open";
    case "carrier_forward":
      return "Bij de vervoerder";
    case "closed":
      return "Gesloten";
    default:
      return "Onbekend";
  }
}

/** "vervoerder reageerde 27 okt 14:05", of "nog geen reactie van de vervoerder". */
export function ticketReactie(iso: string | null | undefined, nu: Date = new Date()): string {
  const wanneer = dagTijdKort(iso, nu);
  return wanneer ? `vervoerder reageerde ${wanneer}` : "nog geen reactie van de vervoerder";
}
